import { useState, useCallback } from 'react';
import { api_call } from '../helpers/untils';

export function useStripeCard({ customerId, email, name } = {}) {
  const [stripePublishableKey, setStripePublishableKey] = useState("");
  const [clientSecret, setClientSecret] = useState(null);
  const [resolvedCustomerId, setResolvedCustomerId] = useState(customerId ?? null);
  const [cards, setCards] = useState([]);
  const [step, setStep] = useState('idle'); // idle | loading | ready | saving | success | error
  const [error, setError] = useState(null);

  // Call when the modal opens — fetches a SetupIntent
  const initSetup = useCallback(async () => {
    setStep('loading');
    setError(null);
    try {
      const res = await api_call(`/api/payment/stripe/cards/setup-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, email, name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setClientSecret(data.clientSecret);
      setResolvedCustomerId(data.customerId);
      setStripePublishableKey(data.STRIPE_PUBLISHABLE_KEY);
      setStep('ready');
    } catch (err) {
      setError(err.message);
      setStep('error');
    }
  }, [customerId, email, name]);

  // Confirm the SetupIntent — saves the card
  const saveCard = useCallback(async ({ stripe, elements, nickname }) => {
    setStep('saving');
    setError(null);
    try {
      const { error: stripeError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/billing`, // fallback for redirect methods
          payment_method_data: {
            ...(nickname ? { metadata: { nickname } } : {}),
          },
        },
        redirect: 'if_required',
      });
      if (stripeError) {
        alert(stripeError.message);
        setError(stripeError.message);
        setStep('error');
        //throw new Error(stripeError.message);
        return false;
      }
      setStep('success');
      return setupIntent;
    } catch (err) {
      setError(err.message);
      setStep('error');
      throw err;
    }
  }, []);

  // Fetch all saved cards for this customer
  const fetchCards = useCallback(async (cid) => {
    const id = cid ?? resolvedCustomerId;
    if (!id) return;
    try {
      const res = await api_call(`/api/payment/stripe/cards/${id}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setCards(data.cards);
    } catch (err) {
      console.error('fetchCards error:', err);
    }
  }, [resolvedCustomerId]);

  // Set a card as default
  const setDefault = useCallback(async (paymentMethodId) => {
    await api_call(`/api/payment/stripe/cards/${resolvedCustomerId}/default`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paymentMethodId }),
    });
    setCards(prev => prev.map(c => ({ ...c, isDefault: c.id === paymentMethodId })));
  }, [resolvedCustomerId]);

  // Delete a card
  const deleteCard = useCallback(async (paymentMethodId) => {
    await api_call(`/api/payment/stripe/cards/${paymentMethodId}`, { method: 'DELETE' });
    setCards(prev => prev.filter(c => c.id !== paymentMethodId));
  }, []);

  const reset = useCallback(() => {
    setClientSecret(null);
    setStep('idle');
    setError(null);
  }, []);

  return {
    step, error, stripePublishableKey, clientSecret, cards,
    customerId: resolvedCustomerId,
    initSetup, saveCard, fetchCards, setDefault, deleteCard, reset,
  };
}